import MenuIcon from "@material-ui/icons/Menu"
import LoginIcon from "@mui/icons-material/Google"
import LogoutIcon from "@mui/icons-material/Logout"
import {
  Alert,
  AppBar,
  Button,
  Drawer,
  IconButton,
  Snackbar,
  Toolbar,
  Typography,
} from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import { getAnalytics, logEvent } from "firebase/analytics"
import { initializeApp } from "firebase/app"
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check"
import {
  AuthCredential,
  getAuth,
  GoogleAuthProvider,
  linkWithCredential,
  OAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  User,
  UserCredential,
} from "firebase/auth"
import {
  doc,
  DocumentSnapshot,
  getFirestore,
  onSnapshot,
  Timestamp,
  Unsubscribe,
} from "firebase/firestore"
import React, { FunctionComponent, useEffect, useRef, useState } from "react"
import { FormattedMessage, IntlProvider } from "react-intl"
import { Link, Route, BrowserRouter as Router, Switch } from "react-router-dom"
import Color from "./Color"
import BusinessProfile from "./components/BusinessProfile"
import BuyerList from "./components/BuyerList"
import Dashboard from "./components/Dashboard"
import DrawerList from "./components/DrawerList"
import HomePage from "./components/HomePage"
import InventoryList from "./components/InventoryList"
import InvoiceList from "./components/InvoiceList"
import InvoiceView from "./components/InvoiceView"
import PrivacyPolicy from "./components/PrivacyPolicy"
import RecordList from "./components/RecordList"
import SubscriptionView from "./components/SubscriptionView"
import { Route as Path, StorageKey, Subscription } from "./Enum"
import { timber_debug } from "./Timber"
import bn from "./translations/bn.json"
import en from "./translations/en.json"
import { UserInfo } from "./Type"

const theme = createTheme({
  palette: {
    primary: {
      main: Color.Primary,
    },
  },
})

const firebaseConfig = {
  apiKey: "AIzaSyCeD-MkrGEG2NdnGwrTwfYejrJedIKc9kk",
  authDomain: "api-project-524648958212.firebaseapp.com",
  databaseURL: "https://api-project-524648958212.firebaseio.com",
  projectId: "api-project-524648958212",
  storageBucket: "api-project-524648958212.appspot.com",
  messagingSenderId: "524648958212",
  appId: "1:524648958212:web:e872233988175bfd9b2851",
  measurementId: "G-F54ZPC8Q3X",
}

const app = initializeApp(firebaseConfig)

// Pass your reCAPTCHA v3 site key (public key) to activate(). Make sure this
// key is the counterpart to the secret key you set in the Firebase console.
const CAPTCHA_SITE_KEY = "6Ldf6p4cAAAAAHJPNpnzaaot0RfgxDCQ4QwnatGi"
initializeAppCheck(app, {
  provider: new ReCaptchaV3Provider(CAPTCHA_SITE_KEY),
  // Optional argument. If true, the SDK automatically refreshes App Check
  // tokens as needed.
  isTokenAutoRefreshEnabled: true,
})

const analytics = getAnalytics()

/**
 * To prevent onAuthStateChanged being called multiple times
 */
let authListenerCalled = false
export type FormattedMessageId = string

type Messages = {
  [locale: string]: {
    [key: string]: string
  }
}

export enum Locale {
  English = "en",
  Bengali = "bn",
}
const language: string = navigator.language.split(/[-_]/)[0] // language without region code
export const messagesByLocale: Messages = { en, bn: { ...en, ...bn } }

const serviceId = "2bcc4ff1925b23dd129b766a55a8079f"

const App: FunctionComponent = () => {
  const firestore = getFirestore()
  const auth = getAuth()
  // auth.languageCode = "bd-bn"

  const getEmptyUserInfo = (): UserInfo => {
    return {
      uid: "",
      name: "",
      email: "",
    }
  }

  const getUserInfo = (user: User): UserInfo => {
    return {
      uid: user.uid ? user.uid : "",
      name: user.displayName ? user.displayName : "",
      email: user.email ? user.email : "",
    }
  }

  const inputRef = useRef(null)
  const [userInfo, setUserInfo] = useState<UserInfo>(getEmptyUserInfo())
  const [snackBarMsg, setSnackBarMsg] = useState<string>("")
  const [locale, setLocale] = useState<Locale>(Locale.Bengali)
  const [drawerState, setDrawerState] = useState<boolean>(false)
  const [subscriber, setSubscriber] = useState<boolean>(false)
  const [startDate, setStartDate] = useState<Timestamp>(Timestamp.now())
  const [endDate, setEndDate] = useState<Timestamp>(Timestamp.now())
  const [product, setProduct] = useState<Subscription>(
    Subscription.FreeSubscription
  )

  useEffect(() => {
    timber_debug("useEffect subscribeAuthStateListener")
    const unsubscribeAuthStateListener = onAuthStateChanged(
      auth,
      (user) => {
        timber_debug("onAuthStateChanged")
        if (!authListenerCalled) {
          authListenerCalled = true
          if (user) {
            timber_debug("onAuthStateChanged user signed in")
            setUserInfo(getUserInfo(user))
            setSnackBarMsg("সাইন-ইন সফল হয়েছে!")
            logEvent(analytics, "web_signed_in")
          } else {
            timber_debug("onAuthStateChanged user signed out")
            setUserInfo(getEmptyUserInfo())
          }
        }
      },
      (error) => {
        console.log(error)
      },
      () => {
        // Called when the listener is removed
        timber_debug("unsubscribeAuthStateListener removed")
      }
    )
    return () => {
      timber_debug("useEffect unsubscribeAuthStateListener")
      unsubscribeAuthStateListener()
    }
  }, [userInfo])

  useEffect(() => {
    timber_debug("useEffect subscriptionListener")
    let unsubscribeSubscriptionListener: Unsubscribe
    if (userInfo.uid !== undefined && userInfo.uid !== "") {
      const docRef = doc(
        firestore,
        `/users/${userInfo.uid}/private`,
        "subscription"
      )
      unsubscribeSubscriptionListener = onSnapshot(
        docRef,
        (docSnap: DocumentSnapshot) => {
          timber_debug("subscribeListener onSnapshot received")
          if (docSnap.exists()) {
            const data = docSnap.data()
            const currentDate = new Date()
            setSubscriber((data.endDate as Timestamp).toDate() > currentDate)
            setStartDate(data.startDate as Timestamp)
            setEndDate(data.endDate as Timestamp)
            setProduct(data.product)
          } else {
            timber_debug("No subscribe doc present!")
          }
        }
      )
    }
    return () => {
      if (unsubscribeSubscriptionListener) {
        timber_debug("useEffect unsubscribeSubscriptionListener")
        unsubscribeSubscriptionListener()
      }
    }
  }, [userInfo])

  const toggleDrawer =
    (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" ||
          (event as React.KeyboardEvent).key === "Shift")
      ) {
        return
      }
      setDrawerState(open)
    }

  const handleSnakbarClose = () => {
    setSnackBarMsg("")
  }

  /**
   * Called from Pricing component
   */
  const onClickSignIn = () => {
    const message =
      locale === Locale.Bengali ? "সফল ভাবে প্রবেশ করেছেন!" : "Signed in!"

    const googleAuthProvider = new GoogleAuthProvider()
    googleAuthProvider.addScope("https://www.googleapis.com/auth/drive.file")

    signInWithPopup(auth, googleAuthProvider)
      .then((result) => {
        timber_debug("signInWithPopup user signedIn")
        setUserInfo(getUserInfo(result.user))
        setSnackBarMsg(message)
        const credential = GoogleAuthProvider.credentialFromResult(result)
        localStorage.setItem(
          StorageKey.AccessToken,
          credential?.accessToken ? credential.accessToken : ""
        )
      })
      .catch((error) => {
        if (error.code === "auth/account-exists-with-different-credential") {
          console.error("FB signIn error", error)
          // User has already signed in using Goggle before.
          // The pending Facebook credential and email address.
          var pendingCred = OAuthProvider.credentialFromError(
            error
          ) as AuthCredential
          console.error("FB signIn pendingCred", pendingCred)
          signInWithPopup(auth, googleAuthProvider)
            .then((result) => {
              // The signed-in user info.
              return linkWithCredential(result.user, pendingCred)
            })
            .then((usercred: UserCredential) => {
              // Facebook account successfully linked to the existing Firebase user.
              const message =
                locale === Locale.Bengali
                  ? "গুগল একাউন্টটি সংযুক্ত হয়েছে!"
                  : "Google account linked successfully!"
              setSnackBarMsg(message)
              setUserInfo(getUserInfo(usercred.user))
            })
            .catch((error) => {
              console.error("FB-Google linkWithCredential", error)
              const message =
                locale === Locale.Bengali
                  ? "গুগল একাউন্টটি সংযুক্ত হয়নি!"
                  : "Failed to link Google account!"
              setSnackBarMsg(message)
            })
        } else {
          console.error("Unhandled FB signIn error", error)
        }
      })
  }

  const onClickSignOut = () => {
    const message =
      locale === Locale.Bengali ? "সফল ভাবে প্রস্থান করেছেন!" : "Signed out!"
    authListenerCalled = false
    signOut(auth)
      .then(() => {
        setUserInfo(getEmptyUserInfo())
        setSnackBarMsg(message)
        // window.location.replace("/");
      })
      .catch((error) => {
        // An error happened.
        console.error(error, "Signout error")
      })
  }

  const handleLocaleSelection = () => {
    if (locale === Locale.English) {
      setLocale(Locale.Bengali)
    } else if (locale === Locale.Bengali) {
      setLocale(Locale.English)
    }
    console.log("locale: ", locale)
  }

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <IntlProvider locale={language} messages={messagesByLocale[locale]}>
          <div>
            <AppBar position="static" style={{ background: Color.Primary }}>
              <Toolbar>
                <IconButton
                  size="large"
                  edge="start"
                  color="inherit"
                  aria-label="menu"
                  sx={{ mr: 2 }}
                  onClick={toggleDrawer(!drawerState)}
                >
                  <MenuIcon />

                  <Drawer
                    anchor={"left"}
                    open={drawerState}
                    onClose={toggleDrawer(false)}
                  >
                    <DrawerList
                      locale={locale}
                      toggleDrawer={toggleDrawer}
                      subscriber={subscriber}
                    />
                  </Drawer>
                </IconButton>
                <Link
                  to="/"
                  style={{
                    flexGrow: 1,
                    textDecoration: "none",
                  }}
                >
                  <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                    <FormattedMessage id="bookkeeper" />
                  </Typography>
                </Link>

                <Button color="inherit" onClick={handleLocaleSelection}>
                  <FormattedMessage id="locale" />
                </Button>
                {userInfo.uid !== "" && (
                  <Button
                    color="inherit"
                    onClick={() => onClickSignOut()}
                    endIcon={<LogoutIcon />}
                  >
                    <FormattedMessage id="logout" />
                  </Button>
                )}
                {userInfo.uid === "" && (
                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={() => {
                      onClickSignIn()
                    }}
                    style={{ whiteSpace: "nowrap" }}
                    endIcon={<LoginIcon />}
                  >
                    <FormattedMessage id="login" />
                  </Button>
                )}
              </Toolbar>
            </AppBar>
            <a
              ref={inputRef}
              id="fmp-button"
              href="#"
              rel={`${serviceId}?cuid=${userInfo.uid}`}
            ></a>

            {/* Page Router */}
            <Switch>
              {/* Privacy Policy. It should be accessible without authentication */}
              <Route path={Path.PrivacyPolicy}>
                <PrivacyPolicy />
              </Route>

              {/* Record list */}
              <Route path={Path.RecordList}>
                {userInfo.uid === "" && (
                  <HomePage locale={locale} signIn={() => onClickSignIn()} />
                )}
                {userInfo.uid !== "" && userInfo.uid !== undefined && (
                  <RecordList userInfo={userInfo} />
                )}
              </Route>

              {/* Dashboard */}
              <Route path={Path.Dashboard}>
                {userInfo.uid === "" && (
                  <HomePage locale={locale} signIn={() => onClickSignIn()} />
                )}
                {userInfo.uid !== "" && userInfo.uid !== undefined && (
                  <Dashboard
                    locale={locale}
                    userName={userInfo.name}
                    subscriber={subscriber}
                    product={product}
                  />
                )}
              </Route>

              {/* Inventory List */}
              <Route path={Path.InventoryList}>
                {userInfo.uid === "" && (
                  <HomePage locale={locale} signIn={() => onClickSignIn()} />
                )}
                {userInfo.uid !== "" && userInfo.uid !== undefined && (
                  <InventoryList userId={userInfo.uid} />
                )}
              </Route>

              {/* Buyer List */}
              <Route path={Path.BuyerList}>
                {userInfo.uid === "" && (
                  <HomePage locale={locale} signIn={() => onClickSignIn()} />
                )}
                {userInfo.uid !== "" && userInfo.uid !== undefined && (
                  <BuyerList userId={userInfo.uid} />
                )}
              </Route>

              {/* Invoice List */}
              <Route exact path={Path.InvoiceList}>
                {userInfo.uid === "" && (
                  <HomePage locale={locale} signIn={() => onClickSignIn()} />
                )}
                {userInfo.uid !== "" && userInfo.uid !== undefined && (
                  <InvoiceList userId={userInfo.uid} />
                )}
              </Route>

              {/* Invoice create view */}
              <Route exact path={`${Path.InvoiceList}/new`}>
                {userInfo.uid === "" && (
                  <HomePage locale={locale} signIn={() => onClickSignIn()} />
                )}
                {userInfo.uid !== "" && userInfo.uid !== undefined && (
                  <InvoiceView userId={userInfo.uid} />
                )}
              </Route>

              {/* Invoice edit view */}
              <Route exact path={`${Path.InvoiceList}/:id`}>
                {userInfo.uid === "" && (
                  <HomePage locale={locale} signIn={() => onClickSignIn()} />
                )}
                {userInfo.uid !== "" && userInfo.uid !== undefined && (
                  <InvoiceView userId={userInfo.uid} />
                )}
              </Route>

              {/* Business profile */}
              <Route path={Path.BusinessProfile}>
                {userInfo.uid === "" && (
                  <HomePage locale={locale} signIn={() => onClickSignIn()} />
                )}
                {userInfo.uid !== "" && userInfo.uid !== undefined && (
                  <BusinessProfile userId={userInfo.uid} />
                )}
              </Route>

              {/* Subscription */}
              <Route path={Path.Subscription}>
                {userInfo.uid === "" && (
                  <HomePage locale={locale} signIn={() => onClickSignIn()} />
                )}
                {userInfo.uid !== "" && userInfo.uid !== undefined && (
                  <SubscriptionView
                    locale={locale}
                    uid={userInfo.uid}
                    subscriber={subscriber}
                    startDate={startDate}
                    endDate={endDate}
                    product={product}
                  />
                )}
              </Route>

              {/* Default homepage */}
              <Route path="/">
                {userInfo.uid === "" && (
                  <HomePage locale={locale} signIn={() => onClickSignIn()} />
                )}
                {userInfo.uid !== "" && userInfo.uid !== undefined && (
                  <Dashboard
                    locale={locale}
                    userName={userInfo.name}
                    subscriber={subscriber}
                    product={product}
                  />
                )}
              </Route>
            </Switch>

            <Snackbar
              open={snackBarMsg !== ""}
              autoHideDuration={4000}
              onClose={handleSnakbarClose}
            >
              <Alert
                variant="filled"
                severity="success"
                sx={{
                  width: "100%",
                  background: Color.PrimaryLight85,
                  color: Color.Black,
                }}
              >
                {snackBarMsg}
              </Alert>
            </Snackbar>
          </div>
        </IntlProvider>
      </Router>
    </ThemeProvider>
  )
}

export default App
